import dayjs from "dayjs";
import { defineStore } from "pinia";
import { computed, ref, shallowRef, toRaw, watch } from "vue";
import { saveAs } from "file-saver";
import { useI18n } from "vue-i18n";
import { isEqual } from "es-toolkit";
import { usePipeScannersStore } from "./usePipeScannersStore";
import { SCANNER_TYPE } from "@/pages/scanner-control/config";
import { unitsConverter } from "@/config/units";
import { filterScanners } from "@/features/pipescanner/helpers/filterScanners";
import { getMinMaxValue } from "@/utils/filterHelpers";
import { auth0 } from "@/config/auth0";
import { getMultipleScannersDetails } from "@/features/pipescanner/api";
import { useAuthStore } from "./useAuthStore";
import ExportXlsx from "@/utils/ExportXlsx";
import { useSettingsStore } from "./useSettingsStore";
import { mergeChanges } from "@/utils/objects";
import type {
  ScannerFilters,
  ScannerIds,
  ScannersExportSettings,
  ScannersService,
} from "@/types/pipeScanners";
import { showToastError } from "@/utils/errors";
import { useStoreUnconfirmedFilters } from "@/utils/storeHelpers";
import type { WidgetsSettingsType } from "@/types/widgets";
import { ScannerControlWidget } from "@/pages/scanner-control/views/ScannerControlMapmode/config";
import { REMAININGS } from "@/constants";
import { useRoute } from "vue-router";

const requests = new Map();

const INITIAL_SCANNER_FILTERS: ScannerFilters = {
  search: "",
  inspectionDateRange: [
    "2020-01-01",
    dayjs().add(1, "day").format("YYYY-MM-DD"),
  ],
  materials: [],
  diameterRange: [-Infinity, Infinity],
  constuctionYearRange: [String(1900), String(new Date().getFullYear())],
};

export const useScannerControlStore = defineStore("scannerControl", () => {
  const pipeScannersStore = usePipeScannersStore();
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();

  const { t } = useI18n();
  const route = useRoute();

  const filters = ref(structuredClone(INITIAL_SCANNER_FILTERS));
  const hasActiveFilters = ref(false);
  watch(
    filters,
    (filters) => {
      const initFiltersWithDiameterRange = {
        ...structuredClone(INITIAL_SCANNER_FILTERS),
        diameterRange: minMaxDiameters.value,
      };
      const hasActiveFiltersNewValue = !isEqual(
        toRaw(filters),
        initFiltersWithDiameterRange
      );
      hasActiveFilters.value = hasActiveFiltersNewValue;
    },
    { deep: true }
  );

  function setFilters(changes: Partial<ScannerFilters>) {
    mergeChanges(filters.value, changes);
    selectedResultScannerIds.value = [];
  }
  function initFilters() {
    setFilters({ diameterRange: minMaxDiameters.value });
  }
  function resetFilters() {
    setFilters(structuredClone(INITIAL_SCANNER_FILTERS));
    initFilters();
  }

  const selectedScannerType = ref(SCANNER_TYPE.RESULTS);

  function setSelectedScannerType(newType) {
    selectedScannerType.value = newType;
  }

  const selectedResultScannerIds = ref<string[]>([]);

  const selectedResultsScannersCount = computed(
    () => selectedResultScannerIds.value.length
  );

  function addSelectedResultScannerId(newId: string) {
    selectedResultScannerIds.value.push(newId);
  }
  function removeSelectedResultScannerId(idToRemove: string) {
    selectedResultScannerIds.value = selectedResultScannerIds.value.filter(
      (id) => id !== idToRemove
    );
  }
  function clearSelectedResultScannerIds() {
    selectedResultScannerIds.value = [];
  }
  function setSelectedResultScannerIds(ids: string[]) {
    selectedResultScannerIds.value = ids;
  }
  function toggleAllResultsScanners() {
    if (isAllResultsScannersSelected.value) {
      clearSelectedResultScannerIds();
      return;
    }

    setSelectedResultScannerIds(filteredResultsScannersIds.value);
  }
  function toggleResultsScannerSelection(id: string) {
    if (selectedResultScannerIds.value.includes(id)) {
      removeSelectedResultScannerId(id);
    } else {
      addSelectedResultScannerId(id);
    }
  }

  const isAwaitingInspectionExport = ref(false);

  function setIsAwaitingInspectionExport(isAwaitingExport: boolean) {
    isAwaitingInspectionExport.value = isAwaitingExport;
  }
  async function exportResultsScanners() {
    try {
      setIsAwaitingInspectionExport(true);

      const inspections = await fetchScannersDetails();

      const settings = {
        ...exportSettings.value,
        language: settingsStore.locale,
      };

      const excelExport = new ExportXlsx(
        inspections,
        0.1,
        15.0,
        settings,
        authStore.selectedGroup,
        settingsStore.units
      );

      const export_wb = await excelExport.generate_export_prom();
      const buffer = await export_wb.writeToBuffer();
      const file = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(file, "Export-" + dayjs().toISOString() + ".xlsx");
    } catch (error) {
      showToastError(error);
    } finally {
      setIsAwaitingInspectionExport(false);
    }
  }

  const activeScannerIds = ref<ScannerIds>({
    [SCANNER_TYPE.RESULTS]: null,
    [SCANNER_TYPE.INFO_PENDING]: null,
    [SCANNER_TYPE.ANALYSIS_PENDING]: null,
  });

  const activeScannerId = computed(
    () => activeScannerIds.value[selectedScannerType.value]
  );
  const activeScanner = computed(() =>
    pipeScannersStore.pipeScanners[selectedScannerType.value].find(
      (scanner) => scanner.id === activeScannerId.value
    )
  );

  function setActiveScannerIds(changes: Partial<ScannerIds>) {
    mergeChanges(activeScannerIds.value, changes);
  }
  function toggleActiveScannerId(id: string) {
    if (activeScannerIds.value[selectedScannerType.value] === id) {
      setActiveScannerIds({ [selectedScannerType.value]: null });
      return;
    }

    setActiveScannerIds({ [selectedScannerType.value]: id });
  }

  const exportSettings = ref<ScannersExportSettings | null>(null);

  function setExportSettings(settings: ScannersExportSettings) {
    exportSettings.value = settings;
  }

  const scannersService = ref<ScannersService | null>(null);

  function setScannersService(service: ScannersService) {
    scannersService.value = service;
  }

  const isScannerControlPageAvailable = computed(() =>
    Boolean(authStore.selectedGroup)
  );

  const convertedResultsScanners = computed(() => {
    const scanners = pipeScannersStore.pipeScanners[SCANNER_TYPE.RESULTS];

    if (settingsStore.units === "metric") {
      return scanners;
    }

    const convertedScanners = scanners.map((scanner) => {
      const scannerCopy = toRaw(scanner);

      ["diameter", "wallthickness_expected_validation"].forEach((key) => {
        scannerCopy[key] = unitsConverter.instance.convert(
          scannerCopy[key],
          "mm"
        );
      });

      Object.values(REMAININGS).forEach((key) => {
        scannerCopy.stats[key] = unitsConverter.instance.convert(
          scannerCopy.stats[key],
          "mm"
        );
      });

      return scannerCopy;
    });

    return convertedScanners;
  });
  const minMaxDiameters = computed<[number, number]>(() => {
    const [min, max] = getMinMaxValue(
      convertedResultsScanners.value,
      "diameter"
    );

    return [Math.floor(min), Math.ceil(max)];
  });
  const filteredResultsScanners = computed(() => {
    const filtered = filterScanners(
      convertedResultsScanners.value,
      SCANNER_TYPE.RESULTS,
      filters.value
    );

    return filtered;
  });
  const filteredResultsScannersIds = computed(() =>
    filteredResultsScanners.value.map((scanner) => scanner.id)
  );
  const filteredInfoPendingScanners = computed(() => {
    return filterScanners(
      pipeScannersStore.pipeScanners[SCANNER_TYPE.INFO_PENDING],
      SCANNER_TYPE.INFO_PENDING,
      filters.value
    );
  });
  const filteredAnalysisPendingScanners = computed(() => {
    return filterScanners(
      pipeScannersStore.pipeScanners[SCANNER_TYPE.ANALYSIS_PENDING],
      SCANNER_TYPE.ANALYSIS_PENDING,
      filters.value
    );
  });
  const filteredPipeScanners = computed(() => {
    return {
      [SCANNER_TYPE.RESULTS]: filteredResultsScanners.value,
      [SCANNER_TYPE.INFO_PENDING]: filteredInfoPendingScanners.value,
      [SCANNER_TYPE.ANALYSIS_PENDING]: filteredAnalysisPendingScanners.value,
    };
  });
  const currentFilteredScanners = computed(
    () => filteredPipeScanners.value[selectedScannerType.value]
  );
  const hasFilteredScanners = computed(() => {
    const hasFilteredResultsScanners =
      filteredResultsScanners.value.length !==
      pipeScannersStore.pipeScanners[SCANNER_TYPE.RESULTS].length;

    const hasFilteredInfoPendingScanners =
      filteredInfoPendingScanners.value.length !==
      pipeScannersStore.pipeScanners[SCANNER_TYPE.INFO_PENDING].length;

    const hasFilteredAnalysisPendingScanners =
      filteredAnalysisPendingScanners.value.length !==
      pipeScannersStore.pipeScanners[SCANNER_TYPE.ANALYSIS_PENDING].length;

    const filtered = {
      [SCANNER_TYPE.RESULTS]: hasFilteredResultsScanners,
      [SCANNER_TYPE.INFO_PENDING]: hasFilteredInfoPendingScanners,
      [SCANNER_TYPE.ANALYSIS_PENDING]: hasFilteredAnalysisPendingScanners,
    };

    return filtered[selectedScannerType.value];
  });
  const isAllResultsScannersSelected = computed(
    () =>
      filteredResultsScanners.value.length > 0 &&
      selectedResultsScannersCount.value ===
        filteredResultsScanners.value.length
  );

  async function fetchPipeScanners() {
    const requestsToFetch: Promise<any>[] = [];

    if (pipeScannersStore.pipeScanners[SCANNER_TYPE.RESULTS].length === 0) {
      const fetchResultsScanners = pipeScannersStore.fetchResultsScanners();
      requestsToFetch.push(fetchResultsScanners);
    }

    if (
      pipeScannersStore.pipeScanners[SCANNER_TYPE.INFO_PENDING].length === 0
    ) {
      const fetchInfoScanners = pipeScannersStore.fetchInfoScanners();
      requestsToFetch.push(fetchInfoScanners);
    }

    if (
      pipeScannersStore.pipeScanners[SCANNER_TYPE.ANALYSIS_PENDING].length === 0
    ) {
      const fetchAnalysisPendingScanners =
        pipeScannersStore.fetchAnalysisPendingScanners();
      requestsToFetch.push(fetchAnalysisPendingScanners);
    }

    await Promise.all(requestsToFetch);
    initFilters();
  }

  async function fetchScannersDetails() {
    try {
      const token = await auth0.getAccessTokenSilently();
      const controller = new AbortController();

      requests.set("multipleScannersDetails", controller);

      const inspection = await getMultipleScannersDetails(
        token,
        authStore.selectedGroup,
        selectedResultScannerIds.value,
        controller
      );

      return inspection;
    } catch (error) {
      showToastError(error);

      throw new Error("fetchScannersDetails");
    }
  }

  const wmsLayers = ref([]);
  const widgetsMapSettings = ref<WidgetsSettingsType>({
    [ScannerControlWidget.ScannersTable]: {
      key: ScannerControlWidget.ScannersTable,
      title: t("pipe_scanner.pipescanners"),
      initialWidth: 840,
      initialHeight: 450,
      getInitialX(container) {
        return container.offsetWidth - ((this.initialWidth as number) + 5);
      },
      getInitialY(container) {
        return container.offsetHeight - ((this.initialHeight as number) + 5);
      },
      initiallyMinimized: true,
    },

    [ScannerControlWidget.WMSWidget]: {
      key: ScannerControlWidget.WMSWidget,
      title: t("dashboard.widgets.color.maximize_title"),
      initialWidth: 350,
      initialHeight: "auto",
      getInitialX(container) {
        return (container.offsetWidth - (this.initialWidth as number)) / 2;
      },
      getInitialY: () => 5,
      initiallyMinimized: false,
      displayConfig: {
        isResizable: false,
      },
    },

    [ScannerControlWidget.WMSLegend]: {
      key: ScannerControlWidget.WMSLegend,
      title: t("wms_layers.layers"),
      initialWidth: 250,
      initialHeight: 365,
      getInitialX: () => 5,
      getInitialY: () => 180,
      initiallyMinimized: false,
      hidden: wmsLayers.value.length === 0,
    },
  });
  function updateWidgetsMapSettings(
    newSettings: typeof widgetsMapSettings.value
  ) {
    Object.entries(newSettings).forEach(([key, value]) => {
      widgetsMapSettings.value[key] = {
        ...widgetsMapSettings.value[key],
        ...value,
      };
    });
  }

  function cancelAllRequests() {
    for (const request of requests.values()) {
      request.abort();
    }
  }
  function resetState() {
    filters.value = structuredClone(INITIAL_SCANNER_FILTERS);
    selectedScannerType.value = SCANNER_TYPE.RESULTS;
    selectedResultScannerIds.value = [];
    isAwaitingInspectionExport.value = false;
    activeScannerIds.value = {
      [SCANNER_TYPE.RESULTS]: null,
      [SCANNER_TYPE.INFO_PENDING]: null,
      [SCANNER_TYPE.ANALYSIS_PENDING]: null,
    };
    exportSettings.value = null;
    scannersService.value = null;
  }

  const {
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
  } = useStoreUnconfirmedFilters(filters);

  const inspectionId = computed(() => route.params.id);
  const inspectionFiles = ref([]);
  function setInspectionFiles(files) {
    inspectionFiles.value = files;
  }
  const bScans = shallowRef(null);
  function setBScans(data) {
    bScans.value = data;
  }
  watch(inspectionId, () => {
    setInspectionFiles([]);
    setBScans(null);
  });

  return {
    filters,
    selectedScannerType,
    selectedResultScannerIds,
    isAwaitingInspectionExport,
    activeScannerIds,
    exportSettings,
    scannersService,
    isScannerControlPageAvailable,
    convertedResultsScanners,
    minMaxDiameters,
    filteredResultsScanners,
    filteredResultsScannersIds,
    filteredInfoPendingScanners,
    filteredAnalysisPendingScanners,
    filteredPipeScanners,
    currentFilteredScanners,
    hasFilteredScanners,
    isAllResultsScannersSelected,
    selectedResultsScannersCount,
    activeScannerId,
    activeScanner,
    setFilters,
    initFilters,
    hasActiveFilters,
    resetFilters,
    setSelectedScannerType,
    addSelectedResultScannerId,
    removeSelectedResultScannerId,
    clearSelectedResultScannerIds,
    setSelectedResultScannerIds,
    toggleAllResultsScanners,
    toggleResultsScannerSelection,
    setIsAwaitingInspectionExport,
    exportResultsScanners,
    setActiveScannerIds,
    toggleActiveScannerId,
    setExportSettings,
    setScannersService,
    fetchPipeScanners,
    wmsLayers,
    widgetsMapSettings,
    updateWidgetsMapSettings,
    fetchScannersDetails,
    cancelAllRequests,
    resetState,
    unconfirmedFilters,
    setUnconfirmedFilters,
    confirmUnconfirmedFilters,
    areNewFiltersUnconfirmed,
    inspectionId,
    inspectionFiles,
    setInspectionFiles,
    bScans,
    setBScans,
  };
});
